import { useState } from 'react';
import { t } from 'i18next';
import { Button, ButtonToggle } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useGetPaymentList } from '../../domains/payments/ui/hooks/useGetPaymentList';
import { useGetMySubscription } from '../../domains/payments/ui/hooks/useGetMySubscription';
import { useGetMyBettieAutoTopUp } from '../../domains/bettie/ui/hooks/useGetMyBettieAutoTopup';
import { usePauseBettieAutoTopUp } from '../../domains/bettie/ui/hooks/usePauseBettieAutoTopUp';
import { useActivateBettieAutoTopUp } from '../../domains/bettie/ui/hooks/useActivateBettieAutoTopup';
import { useRecoverPendingCancel } from '../../domains/payments/ui/hooks/useRecoverPendingCancel';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { formatDateTime } from '../../utils/DateUtil';
import RefundExchangeNotice from './components/RefundExchangeNotice';
import BettiePurchaseHistoryItem from './components/BettiePurchaseHistoryItem';
import EmptyCaseWithTicketImage from './components/EmptyCaseWithTicketImage';
import PopUpBasic from '../components/popup/PopUpBasic';
import BettieSellingListPopup from '../payment/components/BettieSellingListPopup';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import type { components } from '../../types/schema';

type MySubScriptionType = components['schemas']['ApiV1Subscription'];
type BettieAutoTopUpType = components['schemas']['ApiV1BettieAutoTopup'];

const BettiePurchaseHistoryPage = () => {
  const handleHistoryBack = useHandleHistoryBack();
  const { data: payments, isLoading } = useGetPaymentList();
  const { data: mySubscription, isLoading: isMySubscriptionLoading } = useGetMySubscription();
  const { data: bettieAutoTopUp, isLoading: isBettieAutoTopUpLoading } = useGetMyBettieAutoTopUp();
  const { mutateAsync: recoverPendingCancel } = useRecoverPendingCancel();
  const showSuccessToast = useSuccessPhotoCardToast();

  const [isSellingListPopupOpen, setIsSellingListPopupOpen] = useState(false);
  const [isSubscribeAgainPopupOpen, setIsSubscribeAgainPopupOpen] = useState(false);
  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);

  const handleCardClick = () => {
    if (mySubscription?.status === 'pending_cancel') {
      setIsSubscribeAgainPopupOpen(true);
    } else {
      setIsSellingListPopupOpen(true);
    }
  };

  const handleSubscribeAgain = async () => {
    if (!mySubscription?.subscriptionId) {
      return;
    }
    try {
      const response = await recoverPendingCancel({
        subscriptionId: mySubscription.subscriptionId
      });
      if (response?.subscriptionId) {
        setIsSubscribeAgainPopupOpen(false);
        showSuccessToast('bottom', t('BettiePurchaseHistory.successSubscribe') as string);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubscribeAgainPopupOpen(false);
    }
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+32px)]">
      <div className="sticky top-0 flex items-center justify-center px-[16px] py-[10px] bg-background-light dark:bg-background-dark">
        <ArrowLeft
          size={24}
          className="text-text-main-lv1 absolute left-[16px]"
          onClick={handleHistoryBack}
        />
        <p className="col-start-2 text-[17px] font-bold text-text-main-lv1 leading-[20px] text-center">
          {t('BettiePurchaseHistory.title')}
        </p>
      </div>
      {bettieAutoTopUp && !isBettieAutoTopUpLoading && (
        <BettieAutoTopUpCard
          bettieAutoTopUp={bettieAutoTopUp}
          onEdit={() => setIsChargeBottomSheetOpen(true)}
        />
      )}
      {payments?.length === 0 || isMySubscriptionLoading || !mySubscription ? null : (
        <MySubscriptionCard mySubscription={mySubscription} onClick={handleCardClick} />
      )}
      <div className="flex flex-col items-center px-[24px] mt-[28px]">
        {isLoading ? (
          <>
            <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px] flex justify-start w-full mb-[20px]">
              {t('BettiePurchaseHistory.purchaseHistory')}
            </p>
            <div className="w-full flex flex-col gap-[10px]">
              <BettiePurchaseCardSkeleton />
            </div>
          </>
        ) : payments?.length === 0 ? (
          <EmptyCaseWithTicketImage
            title={t('BettiePurchaseHistory.emptyCaseTitle')}
            className="mt-[196px]"
          />
        ) : (
          <>
            <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px] flex justify-start w-full mb-[20px]">
              {t('BettiePurchaseHistory.purchaseHistory')}
            </p>
            <div className="w-full flex flex-col gap-[10px]">
              {payments?.map((item) => (
                <BettiePurchaseHistoryItem key={item.paymentId} item={item} />
              ))}
            </div>
          </>
        )}
        <RefundExchangeNotice className={payments?.length === 0 ? 'mt-[300px]' : 'mt-[100px]'} />
      </div>
      {isSellingListPopupOpen && (
        <BettieSellingListPopup
          onClose={() => setIsSellingListPopupOpen(false)}
          mySubscription={mySubscription}
        />
      )}
      <BettieChargeBottomSheet
        isOpen={isChargeBottomSheetOpen}
        onClose={() => setIsChargeBottomSheetOpen(false)}
      />
      {isSubscribeAgainPopupOpen && mySubscription && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center">
          <PopUpBasic
            type="short"
            title={t('BettiePurchaseHistory.subscribeAgainPopup.title', {
              date: formatDateTime(mySubscription?.nextBillingDate, 'PPP')
            })}
            description={t('BettiePurchaseHistory.subscribeAgainPopup.description')}
            primaryButtonText={t('BettiePurchaseHistory.subscribeAgainPopup.close')}
            secondaryButtonText={t('BettiePurchaseHistory.subscribeAgainPopup.subscribe')}
            onPrimaryButtonClick={() => setIsSubscribeAgainPopupOpen(false)}
            onSecondaryButtonClick={handleSubscribeAgain}
            className="w-[311px]"
          />
        </div>
      )}
    </div>
  );
};

export default BettiePurchaseHistoryPage;

const BettiePurchaseCardSkeleton = () => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: skelton key
          key={index}
          className="w-full px-[20px] py-[16px] bg-background-elevated rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.06),_0_0_12px_0_rgba(0,0,0,0.08)] animate-pulse"
        >
          <div className="w-full flex flex-col gap-[15px] items-start justify-between">
            <div className="h-[14px] w-[80px] rounded-full bg-gray-100 dark:bg-gray-800" />
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-col items-start gap-[6px] min-w-0 flex-1">
                <div className="h-[18px] w-[140px] rounded-full bg-gray-100 dark:bg-gray-800" />
                <div className="h-[16px] w-[180px] rounded-full bg-gray-100 dark:bg-gray-800" />
              </div>
              <div className="h-[19px] w-[60px] rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const BettieAutoTopUpCard = ({
  bettieAutoTopUp,
  onEdit
}: {
  bettieAutoTopUp: BettieAutoTopUpType;
  onEdit: () => void;
}) => {
  const { mutateAsync: pauseAutoTopup, isLoading: isPausing } = usePauseBettieAutoTopUp();
  const { mutateAsync: activateAutoTopup, isLoading: isActivating } = useActivateBettieAutoTopUp();

  const handleToggle = async () => {
    if (isPausing || isActivating) return;
    const id = bettieAutoTopUp.autoTopupId;
    try {
      if (bettieAutoTopUp.status === 'active') {
        await pauseAutoTopup(id);
      } else if (bettieAutoTopUp.status === 'paused') {
        await activateAutoTopup(id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleBusy = isPausing || isActivating;
  const canToggle = bettieAutoTopUp.status === 'active' || bettieAutoTopUp.status === 'paused';

  return (
    <div className="bg-separator1 px-[24px] pt-[20px]">
      <div className="flex items-center justify-between">
        <p className="text-[15px] font-semibold text-text-main-lv2">
          {t('BettiePurchaseHistory.autoTopup')}{' '}
          {bettieAutoTopUp.status === 'active' && (
            <span className="text-primary-light dark:text-primary-dark text-[14px] font-bold">
              {t('BettiePurchaseHistory.active')}
            </span>
          )}
          {bettieAutoTopUp.status === 'paused' && (
            <span className="text-[#FF495A] text-[14px] font-bold">
              {t('BettiePurchaseHistory.paused')}
            </span>
          )}
        </p>
      </div>
      <div className="rounded-[20px] p-[1px] bg-[linear-gradient(102deg,#860EFF_0%,#2DE7D1_44%,#C855FF_100%)] mt-[18px] shadow-[0_0_0_1px_rgba(0,0,0,0.01),0_0_14px_0_rgba(0,0,0,0.08)]">
        <div className="relative rounded-[19px] bg-white dark:bg-black px-[20px] pt-[16px] pb-[18px] flex flex-col items-start gap-[7px]">
          <p className="text-[12px] text-text-main-lv2 leading-[14px]">
            {t('BettiePurchaseHistory.chargeThreshold', {
              count: bettieAutoTopUp.threshold
            })}
          </p>
          <p className="text-[17px] font-semibold text-text-main-lv2 leading-[20px]">
            {t('BettiePurchaseHistory.chargeQuantity', {
              count: bettieAutoTopUp.pointQuantity
            })}
          </p>
          <p className="text-[12px] text-text-main-lv4 leading-[14px]">
            {t('BettiePurchaseHistory.registerDate', {
              date: formatDateTime(bettieAutoTopUp.registeredAt, 'yyyy.MM.dd')
            })}
            {' · '}
            {t('BettiePurchaseHistory.currentCard', {
              cardNumber: 'BC3426'
            })}
          </p>
          <div className="flex items-center gap-[10px] w-full mt-[3px]">
            <button
              type="button"
              className="text-[14px] font-semibold leading-[17px] text-on-secondary bg-secondary rounded-[12px] flex-1 py-[8.5px]"
              onClick={onEdit}
            >
              {t('BettiePurchaseHistory.changeProduct')}
            </button>
            <button
              type="button"
              className="text-[14px] font-semibold leading-[17px] text-on-warning bg-warningContainer rounded-[12px] flex-1 py-[8.5px]"
            >
              {t('BettiePurchaseHistory.changeCard')}
            </button>
          </div>
          <ButtonToggle
            size="lg"
            type="switch"
            checked={bettieAutoTopUp.status === 'active'}
            disabled={!canToggle || toggleBusy}
            className="absolute top-[26px] right-[21px]"
            onChange={handleToggle}
          />
        </div>
      </div>
    </div>
  );
};

const MySubscriptionCard = ({
  mySubscription,
  onClick
}: {
  mySubscription: MySubScriptionType;
  onClick: () => void;
}) => {
  const nextBillingDay = mySubscription.nextBillingDate
    ? new Date(mySubscription.nextBillingDate).getDate()
    : undefined;

  const [title, description] = mySubscription.productName.split('/');

  const pendingCancel = mySubscription.status === 'pending_cancel';

  return (
    <div className="bg-separator1 px-[24px] pt-[24px] pb-[20px]">
      <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
        {t('BettiePurchaseHistory.mySubscription')}
      </p>
      <div className="flex flex-col gap-[15px] px-[20px] py-[16px] bg-background-elevated rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.06),_0_0_12px_0_rgba(0,0,0,0.08)] mt-[20px]">
        <p className="text-[12px] text-text-main-lv2 leading-[14px]">
          {formatDateTime(mySubscription?.startedAt, 'PPP')}
        </p>
        <div className="flex flex-col gap-[6px]">
          <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
            {title.trim()}
            {description && (
              <span className="text-[13px] font-normal ml-[4px]">{description.trim()}</span>
            )}
          </p>
          <p className="text-[12px] text-text-main-lv4 leading-[14px]">
            {pendingCancel
              ? t('BettiePurchaseHistory.expirationDate', {
                  date: formatDateTime(mySubscription?.canceledAt, 'PPP')
                })
              : t('BettiePurchaseHistory.billingDetail', {
                  price: `${Number(mySubscription?.amount).toLocaleString()}원`,
                  date: nextBillingDay ?? '-'
                })}
          </p>
        </div>
        <Button
          size="regular"
          btnColor="secondary"
          className={`w-full text-[14px] font-semibold leading-[17px] rounded-[12px] ${pendingCancel ? '' : 'bg-[#FFE7EA] text-on-warning'}`}
          onClick={onClick}
        >
          {pendingCancel
            ? t('BettiePurchaseHistory.subscribeAgain')
            : t('BettiePurchaseHistory.cancelRequest')}
        </Button>
      </div>
    </div>
  );
};
